import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faPlus, faEdit, faTrashAlt, faExternalLinkAlt,
  faSync, faTimes, faSave, faEye,
} from '@fortawesome/free-solid-svg-icons';
import Alerty from '../../common/Alerty';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'expiring30', label: 'Expiring ≤ 30d' },
  { key: 'expiring7', label: 'Expiring ≤ 7d' },
  { key: 'expired', label: 'Expired' },
];

const today = () => new Date();

const daysUntilExpiry = (endDate) => {
  if (!endDate) return null;
  const ms = new Date(endDate) - today();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

const expiryBadge = (endDate) => {
  const days = daysUntilExpiry(endDate);
  if (days === null) return { label: 'No date', cls: 'bg-gray-100 text-gray-500' };
  if (days < 0) return { label: 'Expired', cls: 'bg-red-100 text-red-700 font-semibold' };
  if (days <= 7) return { label: `${days}d`, cls: 'bg-red-100 text-red-700 font-semibold' };
  if (days <= 30) return { label: `${days}d`, cls: 'bg-amber-100 text-amber-700 font-semibold' };
  return { label: `${days}d`, cls: 'bg-green-100 text-green-700' };
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtNum = (n) => n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

const EMPTY_FORM = {
  plate_no: '',
  insurance_company: '',
  policy_number: '',
  insurance_type: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  premium: '',
  valuation: '',
  contact_person: '',
  contact_phone_number: '',
  valuation_report_link: '',
  insurance_sticker_link: '',
  notes: '',
};

export default function FleetInsurance() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [policies, setPolicies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  const [alert, setAlert] = useState({ color: '', message: '' });
  const [alertOpen, setAlertOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const showAlert = (color, message) => {
    setAlert({ color, message });
    setAlertOpen(true);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [policiesRes, companiesRes, typesRes, carsRes] = await Promise.all([
        axios.get(`${rootUrl}/cars/insurance/current-per-plate`, { headers }),
        axios.get(`${rootUrl}/insurance/companies`, { headers }),
        axios.get(`${rootUrl}/insurance/types`, { headers }),
        axios.get(`${rootUrl}/cars`, { headers }),
      ]);
      setPolicies(policiesRes.data || []);
      setCompanies(companiesRes.data || []);
      setTypes(typesRes.data || []);
      setCars(carsRes.data || []);
    } catch (err) {
      showAlert('red', 'Failed to load insurance data.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- Tab filtering ---
  const filtered = policies.filter((p) => {
    const days = daysUntilExpiry(p.end_date);
    switch (activeTab) {
      case 'active': return days !== null && days >= 0;
      case 'expiring30': return days !== null && days >= 0 && days <= 30;
      case 'expiring7': return days !== null && days >= 0 && days <= 7;
      case 'expired': return days !== null && days < 0;
      default: return true;
    }
  });

  // --- Summary stats ---
  const totalPolicies = policies.length;
  const active = policies.filter(p => { const d = daysUntilExpiry(p.end_date); return d !== null && d >= 0; }).length;
  const expiring30 = policies.filter(p => { const d = daysUntilExpiry(p.end_date); return d !== null && d >= 0 && d <= 30; }).length;
  const expired = policies.filter(p => { const d = daysUntilExpiry(p.end_date); return d !== null && d < 0; }).length;

  // --- Modal helpers ---
  const companyOptions = companies.map(c => ({ value: String(c.id), label: c.name }));
  const typeOptions = types.map(t => ({ value: String(t.id), label: t.name }));
  const carOptions = cars.map(c => ({ value: c.plate_no, label: c.plate_no }));

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors([]);
    setShowModal(true);
  };

  const openEdit = (policy) => {
    setEditingId(policy.id);
    setForm({
      plate_no: policy.plate_no || '',
      insurance_company: policy.insurance_company ? String(policy.insurance_company) : '',
      policy_number: policy.policy_number || '',
      insurance_type: policy.insurance_type ? String(policy.insurance_type) : '',
      start_date: policy.start_date ? policy.start_date.split('T')[0] : '',
      end_date: policy.end_date ? policy.end_date.split('T')[0] : '',
      premium: policy.premium || '',
      valuation: policy.valuation || '',
      contact_person: policy.contact_person || '',
      contact_phone_number: policy.contact_phone_number || '',
      valuation_report_link: policy.valuation_report_link || '',
      insurance_sticker_link: policy.insurance_sticker_link || '',
      notes: policy.notes || '',
    });
    setFormErrors([]);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingId(null); };

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = [];
    if (!form.plate_no) errors.push('Vehicle (Plate No)');
    if (!form.insurance_company) errors.push('Insurance Company');
    if (!form.policy_number) errors.push('Policy Number');
    if (!form.insurance_type) errors.push('Insurance Type');
    if (!form.start_date) errors.push('Start Date');
    if (!form.end_date) errors.push('End Date');
    if (errors.length) { setFormErrors(errors); return; }

    if (new Date(form.end_date) <= new Date(form.start_date)) {
      setFormErrors(['End date must be after start date']);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        plate_no: form.plate_no,
        insurance_company: form.insurance_company,
        policy_number: form.policy_number,
        insurance_type: form.insurance_type,
        start_date: form.start_date,
        end_date: form.end_date,
        premium: form.premium ? parseFloat(form.premium) : null,
        valuation: form.valuation ? parseFloat(form.valuation) : null,
        contact_person: form.contact_person || null,
        contact_phone_number: form.contact_phone_number || null,
        valuation_report_link: form.valuation_report_link || null,
        insurance_sticker_link: form.insurance_sticker_link || null,
        notes: form.notes || null,
        input_by: user?.username,
        edit_by: user?.username,
      };

      if (editingId) {
        await axios.put(`${rootUrl}/cars/insurance/${editingId}`, payload, { headers });
        showAlert('green', 'Insurance record updated successfully.');
      } else {
        await axios.post(`${rootUrl}/cars/insurance`, payload, { headers });
        showAlert('green', 'Insurance record added successfully.');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      showAlert('red', err.response?.data?.error || 'Failed to save insurance record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, plateNo) => {
    if (!window.confirm(`Delete insurance record for ${plateNo}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${rootUrl}/cars/insurance/${id}`, { headers });
      showAlert('green', 'Insurance record deleted.');
      fetchAll();
    } catch (err) {
      showAlert('red', 'Failed to delete insurance record.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">
            <FontAwesomeIcon icon={faShieldAlt} className="text-[#FF5733] mr-3" />
            Fleet Insurance
          </h1>
          <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
          <p className="text-lg text-[#2a2e30] opacity-80">
            Manage and monitor vehicle insurance policies across the fleet
          </p>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
        <Alerty open={alertOpen} setOpen={setAlertOpen} color={alert.color} message={alert.message} />

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow p-5 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Policies</p>
            <p className="text-3xl font-bold text-[#2a2e30]">{totalPolicies}</p>
          </div>
          <div className="bg-white rounded-2xl shadow p-5 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Active</p>
            <p className="text-3xl font-bold text-green-600">{active}</p>
          </div>
          <div className="bg-white rounded-2xl shadow p-5 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Expiring ≤ 30 days</p>
            <p className="text-3xl font-bold text-amber-600">{expiring30}</p>
          </div>
          <div className="bg-white rounded-2xl shadow p-5 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Expired</p>
            <p className="text-3xl font-bold text-red-600">{expired}</p>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <h2 className="text-lg font-bold text-[#2a2e30]">Insurance Policies</h2>
            <div className="flex gap-2">
              <button
                onClick={fetchAll}
                className="p-2 rounded-sm bg-slate-100 hover:bg-slate-200 shadow-none flex items-center justify-center"
                aria-label="Refresh"
              >
                <FontAwesomeIcon icon={faSync} className="w-4 h-4 text-slate-600" />
              </button>
              <button
                onClick={openAdd}
                className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-1 shadow-none flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Policy
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-[#FF5733] text-[#FF5733]'
                    : 'border-transparent text-gray-600 hover:text-[#FF5733]'
                }`}
              >
                <span className="hidden md:inline">{tab.label}</span>
                <span className="md:hidden">{tab.label}</span>
                {tab.key !== 'all' && (
                  <span className="ml-2 text-xs bg-gray-100 rounded-full px-2 py-0.5">
                    {tab.key === 'active' ? active
                      : tab.key === 'expiring30' ? expiring30
                      : tab.key === 'expiring7' ? policies.filter(p => { const d = daysUntilExpiry(p.end_date); return d !== null && d >= 0 && d <= 7; }).length
                      : expired}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF5733]"></div>
              <span className="ml-3 text-gray-500">Loading policies...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FontAwesomeIcon icon={faShieldAlt} className="text-4xl mb-3 opacity-30" />
              <p>No insurance records found for this filter.</p>
            </div>
          ) : (
            <>
              {/* Mobile cards — visible on xs/sm only */}
              <div className="md:hidden space-y-3">
                {filtered.map((p) => {
                  const badge = expiryBadge(p.end_date);
                  return (
                    <div key={p.id} className="bg-white border border-gray-100 rounded-xl shadow-sm p-4">
                      {/* Card header */}
                      <div className="flex items-center justify-between mb-3">
                        <button
                          onClick={() => navigate(`/cars/${p.plate_no}`)}
                          className="text-lg font-bold text-[#FF5733] hover:underline"
                        >
                          {p.plate_no}
                        </button>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>

                      {/* Key details grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm mb-3">
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Company</p>
                          <p className="text-gray-700 font-medium">{p.insurance_company_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Type</p>
                          <p className="text-gray-700">{p.insurance_type_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Policy No</p>
                          <p className="text-gray-700 font-mono text-xs">{p.policy_number || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Premium</p>
                          <p className="text-gray-700 font-semibold tabular-nums text-right">{fmtNum(p.premium)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Start</p>
                          <p className="text-gray-600">{fmtDate(p.start_date)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Expiry</p>
                          <p className="text-gray-600">{fmtDate(p.end_date)}</p>
                        </div>
                        {(p.contact_person || p.contact_phone_number) && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-400 uppercase tracking-wide">Contact</p>
                            <p className="text-gray-600 text-xs">{p.contact_person}{p.contact_person && p.contact_phone_number ? ' · ' : ''}{p.contact_phone_number}</p>
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => navigate(`/cars/insurances/${p.plate_no}`)}
                          title="View history"
                          className="p-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-600"
                          aria-label="View"
                        >
                          <FontAwesomeIcon icon={faEye} className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEdit(p)}
                          title="Edit"
                          className="p-1.5 rounded-sm bg-blue-50 hover:bg-blue-100 text-blue-600"
                          aria-label="Edit"
                        >
                          <FontAwesomeIcon icon={faEdit} className="w-3.5 h-3.5" />
                        </button>
                        {p.insurance_sticker_link && (
                          <a
                            href={p.insurance_sticker_link}
                            target="_blank"
                            rel="noreferrer"
                            title="Insurance sticker"
                            className="p-1.5 rounded-sm bg-green-50 hover:bg-green-100 text-green-600"
                            aria-label="Insurance sticker"
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(p.id, p.plate_no)}
                          title="Delete"
                          className="p-1.5 rounded-sm bg-red-50 hover:bg-red-100 text-red-600 ml-auto"
                          aria-label="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table — visible on md+ only */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-3 font-semibold text-gray-600">Plate No</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Company</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Policy No</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Type</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Start</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Expiry</th>
                      <th className="px-3 py-3 font-semibold text-gray-600 text-right">Premium</th>
                      <th className="px-3 py-3 font-semibold text-gray-600 text-center">Days Left</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Contact</th>
                      <th className="px-3 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, idx) => {
                      const badge = expiryBadge(p.end_date);
                      return (
                        <tr
                          key={p.id}
                          className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/40'}`}
                        >
                          <td className="px-3 py-3 font-semibold text-[#FF5733]">
                            <button
                              onClick={() => navigate(`/cars/${p.plate_no}`)}
                              className="hover:underline"
                            >
                              {p.plate_no}
                            </button>
                          </td>
                          <td className="px-3 py-3 text-gray-700">{p.insurance_company_name || p.insurance_company || '—'}</td>
                          <td className="px-3 py-3 font-mono text-xs text-gray-700">{p.policy_number || '—'}</td>
                          <td className="px-3 py-3 text-gray-700">{p.insurance_type_name || p.insurance_type || '—'}</td>
                          <td className="px-3 py-3 text-gray-600">{fmtDate(p.start_date)}</td>
                          <td className="px-3 py-3 text-gray-600">{fmtDate(p.end_date)}</td>
                          <td className="px-3 py-3 text-right text-gray-700 tabular-nums">{fmtNum(p.premium)}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${badge.cls}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-gray-600 text-xs">
                            {p.contact_person && <div>{p.contact_person}</div>}
                            {p.contact_phone_number && <div className="text-gray-400">{p.contact_phone_number}</div>}
                            {!p.contact_person && !p.contact_phone_number && '—'}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => navigate(`/cars/insurances/${p.plate_no}`)}
                                title="View car insurance history"
                                className="p-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-600"
                                aria-label="View"
                              >
                                <FontAwesomeIcon icon={faEye} className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => openEdit(p)}
                                title="Edit"
                                className="p-1.5 rounded-sm bg-blue-50 hover:bg-blue-100 text-blue-600"
                                aria-label="Edit"
                              >
                                <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                              </button>
                              {p.insurance_sticker_link && (
                                <a
                                  href={p.insurance_sticker_link}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Insurance sticker"
                                  className="p-1.5 rounded-sm bg-green-50 hover:bg-green-100 text-green-600"
                                  aria-label="Insurance sticker"
                                >
                                  <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3 h-3" />
                                </a>
                              )}
                              <button
                                onClick={() => handleDelete(p.id, p.plate_no)}
                                title="Delete"
                                className="p-1.5 rounded-sm bg-red-50 hover:bg-red-100 text-red-600"
                                aria-label="Delete"
                              >
                                <FontAwesomeIcon icon={faTrashAlt} className="w-3 h-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-[#2a2e30]">
                <FontAwesomeIcon icon={faShieldAlt} className="text-[#FF5733] mr-2" />
                {editingId ? 'Edit Insurance Record' : 'Add Insurance Record'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-4">
              {formErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-semibold mb-1">Please fix the following:</p>
                  <ul className="list-disc list-inside text-red-500 text-sm">
                    {formErrors.map(e => <li key={e}>{e}</li>)}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Vehicle */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Vehicle *</label>
                  <Select
                    options={carOptions}
                    value={carOptions.find(o => o.value === form.plate_no) || null}
                    onChange={o => setField('plate_no', o ? o.value : '')}
                    isDisabled={!!editingId}
                    placeholder="Select plate no..."
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Policy Number */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Policy Number *</label>
                  <input
                    type="text"
                    value={form.policy_number}
                    onChange={e => setField('policy_number', e.target.value)}
                    placeholder="e.g. POL-2026-0001"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Insurance Company */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Insurance Company *</label>
                  <Select
                    options={companyOptions}
                    value={companyOptions.find(o => o.value === form.insurance_company) || null}
                    onChange={o => setField('insurance_company', o ? o.value : '')}
                    placeholder="Select company..."
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Insurance Type */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Insurance Type *</label>
                  <Select
                    options={typeOptions}
                    value={typeOptions.find(o => o.value === form.insurance_type) || null}
                    onChange={o => setField('insurance_type', o ? o.value : '')}
                    placeholder="Select type..."
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={e => setField('start_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">End Date *</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={e => setField('end_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Premium */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Premium Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.premium}
                    onChange={e => setField('premium', e.target.value)}
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Valuation */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Vehicle Valuation</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.valuation}
                    onChange={e => setField('valuation', e.target.value)}
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Contact Person */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={form.contact_person}
                    onChange={e => setField('contact_person', e.target.value)}
                    placeholder="Agent name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Contact Phone */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={form.contact_phone_number}
                    onChange={e => setField('contact_phone_number', e.target.value)}
                    placeholder="+254..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Valuation Report Link */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Valuation Report URL</label>
                  <input
                    type="url"
                    value={form.valuation_report_link}
                    onChange={e => setField('valuation_report_link', e.target.value)}
                    placeholder="https://"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Insurance Sticker Link */}
                <div>
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Insurance Sticker URL</label>
                  <input
                    type="url"
                    value={form.insurance_sticker_link}
                    onChange={e => setField('insurance_sticker_link', e.target.value)}
                    placeholder="https://"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>

                {/* Notes */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[#2a2e30] mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={e => setField('notes', e.target.value)}
                    rows={2}
                    placeholder="Additional notes..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-vertical focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeModal}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faSave} />
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
